import React, { useMemo } from "react"
import useFetchTemplates from "../../../hooks/useFetchTemplates"
import {
	convertMinutesTo12HHMM,
	getDayOfWeek,
	convertTimeToMinutes
} from "../../../utils/generalUtils"

const UtilizationSummary = ({ day }) => {
	const { templatesData: templates } = useFetchTemplates()

	const chairUtil = day.metrics?.chair_time_utilization || 0
	const levelLoadScore = day.metrics?.level_load_score || 0
	const adherence = day.metrics?.template_compliance || 0

	// Get actual patient data
	const scheduleData = day.graphData?.utilizationCurves?.scheduleData || []

	// Create a map of time slots to their utilization data
	const scheduleMap = useMemo(() => {
		const map = {}
		scheduleData.forEach(slot => {
			const time = typeof slot.time === "string" ? parseInt(slot.time, 10) : slot.time
			map[time] = slot
		})
		return map
		// `scheduleData` is derived inline from `day.graphData` each render but is stable in practice; wrapping adds noise.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scheduleData])

	// Get template utilization from Redux
	const templateUtilization = () => {
		if (!templates) return {}

		const dayOfWeek = getDayOfWeek(day.date)

		const template = templates.find(t => t.day_of_week === dayOfWeek && t.endDate === null)

		if (!template) return {}

		// Convert template schedule to hourly utilization
		const utilization = {}
		template.schedule.forEach(slot => {
			const timeInMinutes = convertTimeToMinutes(slot.time)
			if (timeInMinutes % 60 === 0) {
				utilization[timeInMinutes] = slot.expectedPatients || 0
			}
		})
		return utilization
	}

	// Find peak hours where actual patients exceed template
	const peakHours = useMemo(() => {
		const peaks = []
		const templateData = templateUtilization()
		Object.entries(templateData).forEach(([time, scheduled]) => {
			const timeInMinutes = parseInt(time)
			const slot = scheduleMap[timeInMinutes]
			const actualPatients = slot?.ongoing || 0

			if (actualPatients > scheduled) {
				peaks.push({
					time: timeInMinutes,
					timeFormatted: convertMinutesTo12HHMM(timeInMinutes),
					scheduled,
					actual: actualPatients,
					excess: actualPatients - scheduled,
					starts: slot?.starts || 0,
					acuity: slot?.acuity || 0
				})
			}
		})

		// Sort peak hours by time
		return peaks.sort((a, b) => a.time - b.time)
		// `templateUtilization` is a helper defined inline but stable in practice.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateUtilization, scheduleMap])

	const metricTooltips = {
		levelLoad: "Measures how well patient load is distributed across the day",
		chairUtil: "Percentage of chairs being utilized during operating hours",
		adherence: "How closely the schedule follows the template guidelines"
	}

	return (
		<div className="bg-white rounded-lg shadow p-6 flex flex-col">
			<h3 className="text-lg font-semibold mb-4">Utilization Summary</h3>
			<div className="flex flex-col space-y-4">
				<div className="flex items-center">
					<div className="w-48">
						<div className="flex items-center">
							<span>Chair Utilization</span>
							<div className="relative inline-block ml-2 group">
								<span className="text-gray-400 cursor-help">ⓘ</span>
								<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
									{metricTooltips.chairUtil}
								</div>
							</div>
						</div>
					</div>
					<div className="flex-1 mx-4 mr-2 h-2 bg-gray-200 rounded-full">
						<div
							className="h-2 rounded-full bg-[#6955EA]"
							style={{ width: `${chairUtil}%` }}
						></div>
					</div>
					<span className="w-12 text-right font-bold">{chairUtil}%</span>
				</div>
				<div className="flex items-center">
					<div className="w-48">
						<div className="flex items-center">
							<span>Template Compliance</span>
							<div className="relative inline-block ml-2 group">
								<span className="text-gray-400 cursor-help">ⓘ</span>
								<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
									{metricTooltips.adherence}
								</div>
							</div>
						</div>
					</div>
					<div className="flex-1 mx-4 mr-2 h-2 bg-gray-200 rounded-full">
						<div
							className="h-2 rounded-full bg-[#6955EA]"
							style={{ width: `${adherence}%` }}
						></div>
					</div>
					<span className="w-12 text-right font-bold">{adherence}%</span>
				</div>
				<div className="flex items-center">
					<div className="w-48">
						<div className="flex items-center">
							<span>Level Load Score</span>
							<div className="relative inline-block ml-2 group">
								<span className="text-gray-400 cursor-help">ⓘ</span>
								<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
									{metricTooltips.levelLoad}
								</div>
							</div>
						</div>
					</div>
					<div className="flex-1 mx-4 mr-2 h-2 bg-gray-200 rounded-full">
						<div
							className="h-2 rounded-full bg-[#6955EA]"
							style={{ width: `${levelLoadScore}%` }}
						></div>
					</div>
					<span className="w-12 text-right font-bold">{levelLoadScore}%</span>
				</div>
				<div className="mt-4">
					<div className="flex items-center mb-2">
						<span className="mr-2">⚠️</span>
						<span className="font-semibold">
							Peak Hours - Exceeding Template Capacity
						</span>
					</div>
					{peakHours.length > 0 ? (
						<div className="max-h-[225px] overflow-y-scroll pr-2 space-y-2 custom-scrollbar [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full">
							{peakHours.map((peak, index) => (
								<div
									key={index}
									className="bg-red-50 border border-red-100 rounded-lg p-3"
								>
									<div className="flex justify-between items-center">
										<span className="font-medium text-red-800">
											{peak.timeFormatted}
										</span>
										<span className="text-red-600 font-semibold bg-red-100 px-2 py-1 rounded">
											+{peak.excess} over template
										</span>
									</div>
									<div className="grid grid-cols-2 gap-2 mt-2 text-sm">
										<div className="bg-white p-2 rounded border border-gray-100">
											<div className="text-gray-500 text-xs">
												Template Capacity
											</div>
											<div className="font-medium">
												{peak.scheduled} patients
											</div>
										</div>
										<div className="bg-white p-2 rounded border border-gray-100">
											<div className="text-gray-500 text-xs">
												Scheduled Patients
											</div>
											<div className="font-medium text-red-600">
												{peak.actual} patients
											</div>
										</div>
									</div>
									<div className="mt-2 text-xs text-gray-500">
										This hour exceeds the template's recommended patient
										capacity by {peak.excess} patients
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-gray-500 text-sm py-2 bg-green-50 border border-green-100 rounded-lg p-3">
							<div className="flex items-center">
								<span className="mr-2">✅</span>
								<span>All hours are within template capacity</span>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default UtilizationSummary
